/**
 * Monetary-policy timeseries mini-section -- "Fed Monetary Policy Outlook"
 * dashboard teaser.
 *
 *   - Runs the EM-estimated Kalman filter on daily-aggregated scores
 *   - Renders: uncertainty band, Kalman trend line, raw document scatter
 *   - Palette: teal (#203C44 line, rgba(32,60,68,0.1) fill)
 *   - Direction annotations: arrows + "Tightening Bias" / "Easing Bias"
 *   - Y-axis: data-driven with 1.5% padding on each side
 */
import { useMemo } from 'react';
import DashboardCard from '@/features/dashboard/components/DashboardCard';
import QueryState from '@/components/QueryState';
import PlotlyChart from '@/lib/plotly/PlotlyChart';
import { AXIS_LINE } from '@/lib/plotly/defaults';
import { useMonetaryPolicyTimeseries } from '@/features/dashboard/api';
import type { TimeseriesRecord } from '@/features/dashboard/types';
import type { Data, Layout, Shape } from 'plotly.js';
import { smoothSeries, type Observation } from '@/lib/utils/kalman';
import { autoRangeYAxis } from '@/lib/plotly/yAxisRange';
import { isoDay } from '@/lib/utils/dates';

/**
 * Teal palette: `line` (#203C44 dark teal) used for scatter dots and the
 * Kalman trend; `fill` (rgba(32,60,68,0.1)) for the uncertainty band.
 */
const PALETTE = {
  line: '#203C44',
  fill: 'rgba(32,60,68,0.1)',
};

/**
 * Aggregate same-day scores into averages and a count vector.
 */
function buildDailyAggregate(
  start: string,
  end: string,
  records: readonly TimeseriesRecord[],
): { allDays: string[]; scores: Observation[]; counts: number[] } {
  const perDay = new Map<string, number[]>();
  for (const r of records) {
    if (r.score == null || !Number.isFinite(r.score)) continue;
    const day = r.date.slice(0, 10);
    const bucket = perDay.get(day);
    if (bucket) bucket.push(r.score);
    else perDay.set(day, [r.score]);
  }
  const allDays: string[] = [];
  const scores: Observation[] = [];
  const counts: number[] = [];
  const cursor = new Date(start);
  const stop = new Date(end);
  while (cursor <= stop) {
    const d = isoDay(cursor);
    allDays.push(d);
    const vals = perDay.get(d);
    if (vals && vals.length > 0) {
      scores.push(vals.reduce((a, b) => a + b, 0) / vals.length);
      counts.push(vals.length);
    } else {
      scores.push(null);
      counts.push(0);
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return { allDays, scores, counts };
}

/** Narrow records to the display window. */
function filterDisplay(
  records: readonly TimeseriesRecord[],
  start: Date,
  end: Date,
): TimeseriesRecord[] {
  return records.filter((r) => {
    const d = new Date(r.date.slice(0, 10));
    return d >= start && d <= end;
  }) as TimeseriesRecord[];
}

/**
 * Kalman-grid start: the earliest fetched record. The grid runs over the
 * full history (not a window around the display range) so this mini chart
 * shows the same trend as the Central Bank timeseries page.
 */
function pickKalmanStart(records: readonly TimeseriesRecord[]): string {
  let earliest: string | undefined;
  for (const r of records) {
    const d = r.date.slice(0, 10);
    if (earliest === undefined || d < earliest) earliest = d;
  }
  return earliest ?? isoDay(new Date());
}

/** Compute nticks: `Math.max(4, Math.min(8, floor(days / 90)))`. */
function computeNticks(start: string, end: string): number {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  const days = ms / (1000 * 60 * 60 * 24);
  return Math.max(4, Math.min(8, Math.floor(days / 90)));
}

export default function MPTimeseriesMiniSection(): JSX.Element {
  const query = useMonetaryPolicyTimeseries('10y');
  const records = query.data?.records;

  const { traces, layout } = useMemo(() => {
    if (!records || records.length === 0) {
      return { traces: [] as Data[], layout: {} as Partial<Layout> };
    }

    // Display window: 10 years back through 2 days ahead of today.
    const displayEnd = new Date();
    displayEnd.setDate(displayEnd.getDate() + 2);
    const displayStart = new Date();
    displayStart.setFullYear(displayStart.getFullYear() - 10);

    const startStr = isoDay(displayStart);
    const endStr = isoDay(displayEnd);

    // Extend the x-axis 30 days past the end date for breathing room.
    const gapDate = new Date(displayEnd);
    gapDate.setDate(gapDate.getDate() + 30);
    const gapStr = isoDay(gapDate);

    const displayRecs = filterDisplay(records, displayStart, displayEnd);
    const resultTraces: Data[] = [];

    // Kalman smoother: uncertainty band + trend line
    if (records.length > 1) {
      const kalmanStart = pickKalmanStart(records);
      const { allDays, scores, counts } = buildDailyAggregate(kalmanStart, endStr, records);
      const { mean, variance } = smoothSeries(scores, counts);

      const sd = variance.map((v) => Math.sqrt(v));
      const lower = mean.map((m, i) => (m != null ? m - sd[i]! : null));
      const upper = mean.map((m, i) => (m != null ? m + sd[i]! : null));

      // Uncertainty band lower bound (invisible line for tonexty fill anchor)
      resultTraces.push({
        type: 'scatter',
        mode: 'lines',
        x: allDays,
        y: lower as (number | null)[],
        line: { width: 0 },
        hoverinfo: 'skip',
        showlegend: false,
        name: 'Uncertainty (lower)',
      });

      // Uncertainty band upper bound + fill
      resultTraces.push({
        type: 'scatter',
        mode: 'lines',
        x: allDays,
        y: upper as (number | null)[],
        line: { width: 0 },
        fill: 'tonexty',
        fillcolor: PALETTE.fill,
        hoverinfo: 'skip',
        name: 'Uncertainty Bands',
      });

      // Kalman trend line
      resultTraces.push({
        type: 'scatter',
        mode: 'lines',
        x: allDays,
        y: mean,
        line: { width: 2, color: PALETTE.line },
        hoverinfo: 'skip',
        name: 'Trend',
      });
    }

    // Raw document scatter -- marker size 4, opacity 0.6.
    if (displayRecs.length > 0) {
      resultTraces.push({
        type: 'scatter',
        mode: 'markers',
        x: displayRecs.map((r) => r.date.slice(0, 10)),
        y: displayRecs.map((r) => r.score),
        marker: { color: PALETTE.line, size: 4, opacity: 0.6 },
        name: 'Documents',
        customdata: displayRecs.map((r) => [
          r.speaker_name ?? '',
          r.document_type ?? '',
        ]) as unknown as string[][],
        hovertemplate:
          'Speaker: %{customdata[0]}<br>' +
          'Type: %{customdata[1]}<br>' +
          'Date: %{x|%d %b %Y}<br>' +
          'Score: %{y:.3f}<extra></extra>',
      });
    }

    // Y-axis range: 1.5% padding on each side.
    const yRange = autoRangeYAxis(
      displayRecs.map((r) => r.score),
      undefined,
      { paddingPct: 0.015, fallback: [0, 1] },
    );

    // Direction annotations: arrows + rotated labels.
    const annotations = [
      {
        x: 1,
        y: 0.98,
        xref: 'paper',
        yref: 'paper',
        text: '↑',
        showarrow: false,
        font: { size: 16, color: '#666' },
        textangle: 0,
      },
      {
        x: 1,
        y: 0.02,
        xref: 'paper',
        yref: 'paper',
        text: '↓',
        showarrow: false,
        font: { size: 16, color: '#666' },
        textangle: 0,
      },
      {
        x: 0.965,
        y: 0.82,
        xref: 'paper',
        yref: 'paper',
        text: 'Tightening Bias',
        showarrow: false,
        font: { size: 10, color: '#666' },
        textangle: -90,
        xanchor: 'left',
        yanchor: 'middle',
        xshift: 15,
      },
      {
        x: 0.965,
        y: 0.18,
        xref: 'paper',
        yref: 'paper',
        text: 'Easing Bias',
        showarrow: false,
        font: { size: 10, color: '#666' },
        textangle: -90,
        xanchor: 'left',
        yanchor: 'middle',
        xshift: 15,
      },
    ] as unknown as Layout['annotations'];

    const resultLayout: Partial<Layout> = {
      title: {
        text: 'Monetary Policy Guidance',
        font: { size: 16 },
        y: 0.99,
      },
      hovermode: 'closest',
      legend: {
        orientation: 'h',
        x: 0.5,
        xanchor: 'center',
        y: 1.01,
        yanchor: 'bottom',
        // Legacy `timeseries-component.js` leaves this legend at the Plotly
        // default 12; pin it so the shared default (now 16) does not enlarge it.
        font: { size: 12 },
      },
      font: { family: 'Gill Sans MT' },
      paper_bgcolor: '#ffffff',
      plot_bgcolor: '#ffffff',
      margin: { t: 50, r: 20, b: 40, l: 40 },
      annotations,
      xaxis: {
        tickformat: '%b %Y',
        tickangle: 0,
        range: [startStr, gapStr],
        ...AXIS_LINE,
        fixedrange: true,
        nticks: computeNticks(startStr, endStr),
      },
      yaxis: {
        title: { text: 'Score' },
        ...AXIS_LINE,
        fixedrange: true,
        range: yRange,
      },
      shapes: [] as Partial<Shape>[],
    };

    return { traces: resultTraces, layout: resultLayout };
  }, [records]);

  return (
    <DashboardCard
      title="Fed Monetary Policy Outlook"
      href="/central-bank/timeseries"
      hrefLabel="Know More"
    >
      {/* Light teal-tinted strip with the italic description line. */}
      <div className="my-1 px-2 py-2 rounded bg-[rgba(78,183,172,0.05)]">
        <p className="m-0 italic text-[0.8rem] text-[#6B7280] font-[var(--font-body)]">
          Interactive time series showing Fed forward-looking monetary policy guidance
        </p>
      </div>
      <QueryState
        query={query}
        isEmpty={(payload) => !payload.records || payload.records.length === 0}
        loadingLabel="Loading"
        errorLabel="Failed to load timeseries."
        emptyLabel="No timeseries data available."
      >
        {() => (
          // 350px chart area. All three of `height` / `minHeight` /
          // `maxHeight` are pinned + `overflow: hidden` so the chart can NOT
          // grow when the body briefly toggles `overflow: hidden` on modal
          // open (scrollbar appears/disappears and Plotly's resize handler
          // fires a relayout). Without the lock, the card visibly grows
          // taller after closing a topic modal.
          <div
            className="w-full overflow-hidden"
            style={{
              height: 350,
              minHeight: 350,
              maxHeight: 350,
              padding: '0.25rem',
            }}
          >
            <PlotlyChart data={traces} layout={layout} config={{ displayModeBar: false }} />
          </div>
        )}
      </QueryState>
    </DashboardCard>
  );
}
